import { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import {
  CorpTypeList3,
  formClassCorp,
  FormKbn,
  Prefectures,
  YesOrNoFlug,
  OkOrNoFlug,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { IAPI_CON024_Response, IAPI_CON029_Request } from "../../type/apiType";
import { TkGetCode } from "../../utils/TkGetCode";
import TkImportantMattersModal from "./components";
import { httpAPI_CON024, httpAPI_CON029 } from "./api/request";
import { fakeTableList, schema, stepper } from "./parts";
import _ from "lodash";

/**
 * SCRC030102 新規契約情報入力（法人情報）
 * 新規契約に必要な法人情報を登録する。
 * @return SCRC030102
 */
const SCRC030102 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [resData, setResData] = useState<IAPI_CON024_Response>({});

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);

  const [isRegister, setIsRegister] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [confirmationDate, setConfirmationDate] = useState("");

  const [tel, setTel] = useState("");
  const [fax, setFax] = useState("");
  const [managerTel, setManagerTel] = useState("");

  const [delay, setDelay] = useState<boolean>(false);
  const [contracting, setContracting] = useState<boolean>(false);
  const [pastContract, setPastContract] = useState<boolean>(false);

  const applyId = location.state?.applyId ?? businessState?.job_01?.applyId;

  const [statecorpInfoList, setStatecorpInfoList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const isKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;

  // 法人施設職員新規契約情報参照API実行
  const handleCallApiInit = async () => {
    try {
      const res = await httpAPI_CON024(
        isKiko
          ? {
              applyId,
              contractorNo: businessState?.common?.contractorNo,
            }
          : {
              applyId,
            }
      );

      let resArr = stateFakeTableList.current;

      if (res.resultCd === "I0000") {
        // 初期表示
        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          targetObj && (targetObj.setDefaultValue = res[i as keyof typeof res]);
        }

        let resObj = {
          prefectures: TkGetCode(Prefectures, res.prefectureCd),
          city: res.city || "",
          address: res.addressOther || "",
        };
        resArr.find((item) => item.keyword === "address")!.valueObject = resObj;

        res.formList?.forEach((file: any) => {
          let targetObj = resArr.find(
            (item: any) =>
              file.formKbn === FormKbn["法人書類"] &&
              file.formClass === item.keyword
          )!;
          let fileObj = [
            { formName: file?.fileName, fileBase64: file?.fileBase64 },
          ];

          targetObj && (targetObj.setDefaultValue = fileObj);
        });

        // レスポンスパラメーターから値が取得できない場合の初期セット
        if (!res.businessOwnerClass) {
          resArr.find(
            (item) => item.keyword === "businessOwnerClass"
          )!.setDefaultValue = CorpTypeList3["社会福祉法人"];
        }

        if (!res.applyDate) {
          const applyDate = resArr.find(
            (item) => item.keyword === "applyDate"
          )!;
          applyDate.setDefaultValue = dayjs().format("YYYY/MM/DD");
        }

        if (res?.pastContractFlg === "0") {
          resArr.find(
            (item) => item.keyword === "pastContractFlg"
          )!.setDefaultValue = OkOrNoFlug["いいえ"];
        } else if (res?.pastContractFlg === "1") {
          resArr.find(
            (item) => item.keyword === "pastContractFlg"
          )!.setDefaultValue = OkOrNoFlug["はい"];
        }

        if (res?.contractFlg === "0") {
          resArr.find((item) => item.keyword === "smeRmaFlg")!.setDefaultValue =
            YesOrNoFlug["無"];
        } else if (res?.contractFlg === "1") {
          resArr.find((item) => item.keyword === "smeRmaFlg")!.setDefaultValue =
            YesOrNoFlug["有"];
        }

        if (res?.salaryPaymentDelayFlg === "0") {
          resArr.find(
            (item) => item.keyword === "payrollDeferredFlg"
          )!.setDefaultValue = YesOrNoFlug["無"];
        } else if (res?.salaryPaymentDelayFlg === "1") {
          resArr.find(
            (item) => item.keyword === "payrollDeferredFlg"
          )!.setDefaultValue = YesOrNoFlug["有"];
        }

        setResData(res);
        setPastContract(res.pastContractFlg === OkOrNoFlug["はい"]);

        setConfirmed(!!res.confirmationDate);
        setConfirmationDate(
          res.confirmationDate ?? dayjs().format("YYYY/MM/DD")
        );

        setStatecorpInfoList([...resArr]);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;

    let postcode = resArr.find((item) => item.keyword === "zipcode")!;
    let tel = resArr.find((item) => item.keyword === "tel")!;
    let fax = resArr.find((item) => item.keyword === "fax")!;
    let managerTel = resArr.find((item) => item.keyword === "managerTel")!;
    let pastContract = resArr.find(
      (item) => item.keyword === "pastContractFlg"
    )!;
    let pastContractCancelDate = resArr.find(
      (item) => item.keyword === "pastContractCancelDate"
    )!;
    let nowContract = resArr.find((item) => item.keyword === "smeRmaFlg")!;
    let paymentDelay = resArr.find(
      (item) => item.keyword === "payrollDeferredFlg"
    )!;

    // 住所検索
    postcode.getPostalCodeValue = (value: any) => {
      const data = value.value;

      let resObj = {
        prefectures: data?.address1,
        city: `${data?.address2}${data?.address3}`,
        address: "",
      };

      resArr.find((item) => item.keyword === "address")!.valueObject = resObj;

      setState({
        ...state,
        errorMsgId: "",
      });
    };

    postcode.getPostalCodeError = (value: any) => {
      setState({
        ...state,
        errorMsgId: value,
      });
    };

    tel.onGetInputValue = (value: any) => {
      setTel(value.target.valueForShow);
    };

    fax.onGetInputValue = (value: any) => {
      setFax(value.target.valueForShow);
    };

    managerTel.onGetInputValue = (value: any) => {
      setManagerTel(value.target.valueForShow);
    };

    // 「契約解除日」の表示/非表示
    pastContract.onGetValue = (data: any) => {
      setPastContract(data === YesOrNoFlug["有"]);
      pastContractCancelDate.setDefaultValue = "";
    };

    // 「調整のため、機構にお問い合わせください。」の表示/非表示
    nowContract.onGetValue = (data: any) => {
      setContracting(data === YesOrNoFlug["有"]);
    };

    paymentDelay.onGetValue = (data: any) => {
      setDelay(data === YesOrNoFlug["有"]);
    };

    setStatecorpInfoList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // 重要事項確認モーダル
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let nodeButtonImportant = resArr.find(
      (item) => item.keyword === "nodeButtonImportant"
    )!;

    nodeButtonImportant.node = (
      <TkImportantMattersModal
        isRegister={setIsRegister}
        confirmed={confirmed}
      />
    );

    setStatecorpInfoList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed]);

  // テーブルの表示/非表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let lineTitleCyouSei = resArr.find(
      (item) => item.keyword === "lineTitleCyouSei"
    )!;
    let lineTitleImportant = resArr.find(
      (item) => item.keyword === "lineTitleImportant"
    )!;
    let nodeButtonImportant = resArr.find(
      (item) => item.keyword === "nodeButtonImportant"
    )!;
    let lineTitleImportantConfirm = resArr.find(
      (item) => item.keyword === "lineTitleImportantConfirm"
    )!;
    let flag = false;

    if (delay || contracting) {
      flag = false;
    } else {
      flag = true;
    }

    lineTitleCyouSei.showLine = !flag;
    lineTitleImportant.showLine = flag;
    nodeButtonImportant.showLine = flag;
    lineTitleImportantConfirm.showLine = flag;

    setStatecorpInfoList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, contracting]);

  // 「契約解除日」の表示/非表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;

    resArr.find((item) => item.keyword === "pastContractCancelDate")!.showLine =
      pastContract;

    setStatecorpInfoList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastContract]);

  const makeHttpObj = (obj: any): IAPI_CON029_Request => {
    let fileData: any = [];
    if (obj[formClassCorp["履歴事項全部証明書（写）"]]?.length) {
      const data1 = {
        serialNo: 0,
        formKbn: FormKbn["法人書類"],
        formClass: formClassCorp["履歴事項全部証明書（写）"],
        fileName: obj[formClassCorp["履歴事項全部証明書（写）"]][0].formName,
        fileBase64:
          obj[formClassCorp["履歴事項全部証明書（写）"]][0].fileBase64,
      };
      fileData.push(data1);
    }

    if (obj[formClassCorp["就業規則"]]?.length) {
      const data2 = {
        serialNo: 0,
        formKbn: FormKbn["法人書類"],
        formClass: formClassCorp["就業規則"],
        fileName: obj[formClassCorp["就業規則"]][0].formName,
        fileBase64: obj[formClassCorp["就業規則"]][0].fileBase64,
      };
      fileData.push(data2);
    }

    if (obj[formClassCorp["給与規程"]]?.length) {
      const data3 = {
        serialNo: 0,
        formKbn: FormKbn["法人書類"],
        formClass: formClassCorp["給与規程"],
        fileName: obj[formClassCorp["給与規程"]][0].formName,
        fileBase64: obj[formClassCorp["給与規程"]][0].fileBase64,
      };
      fileData.push(data3);
    }

    fileData.forEach((item: any, index: any) => (item.serialNo = index + 1));

    const reqObj: IAPI_CON029_Request = {
      contractorNo:
        state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
          ? businessState.common?.contractorNo
          : state.authInfo?.contractorNo,
      applyId: applyId,
      corpName: obj.corpName,
      corpNameKana: obj.corpNameKana,
      corpNumber: obj.corpNumber,
      directorTitle: obj.directorTitle,
      directorName: obj.directorName,
      zipcode: obj.zipcode,
      prefectureCd:
        Prefectures[obj.address.prefectures as keyof typeof Prefectures],
      addressCity: obj.address.city,
      addressOthers: obj.address.address,
      businessOwnerClass: obj.businessOwnerClass,
      tel: tel,
      fax: fax,
      fileList: fileData,
      managerDeptName: obj.managerDeptName,
      managerPosition: obj.managerPosition,
      managerName: obj.managerName,
      managerTel: managerTel,
      managerEmail: obj.managerEmail,
      applyDate: obj.applyDate,
      pastContractFlg: obj.pastContractFlg,
      pastContractCancelDate: obj.pastContractCancelDate,
      payrollDeferredFlg: obj.payrollDeferredFlg,
      confirmationDate: confirmationDate,
    };

    return reqObj;
  };

  const onCheckAndSubmit = async (data: any) => {
    //契約者申請情報更新API実行
    try {
      const res = await httpAPI_CON029({
        ...makeHttpObj(data),
      });

      if (res.resultCd === "I0000") {
        // 持回情報
        setBusinessState({
          common: {
            ...businessState.common,
            businessType: "JOB-01",
          },
          job_01: {
            ...businessState.job_01,
            applyId: res.applyId,
            desiredContractDate: data.applyDate,
            tempSaveStep: resData.tempSaveStep,
            businessOwnerClass: data.businessOwnerClass,
          },
        });

        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRC050102");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box data-testid="SCRC030102PageForTest">
      <TkStepper steps={stepper} savaStep={resData.tempSaveStep} />

      <TkFakeTable
        list={statecorpInfoList}
        ruleSchema={schema}
        onCheckAndSubmit={onCheckAndSubmit}
        buttonGroup={<TkBackButton />}
        submitButtonText="確認"
        disabled={!isRegister}
        showButton={!delay && !contracting}
      />
    </Box>
  );
};

export default SCRC030102;
